import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setPassword, clearError } from '../features/auth/authSlice';
import './CreatePassword.css';
import logo from '../assets/xtrafyls.svg';
import { signin_profile, passwordIcon } from '../assets';

const CreatePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { isLoading, error } = useSelector((state) => state.auth);
  
  const [password, setPasswordInput] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    dispatch(clearError());

    // Validate passwords
    if (!password || !confirmPassword) {
      setErrors({ general: 'Please fill in all fields' });
      return;
    }

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setErrors({ password: passwordErrors[0] });
      return;
    }
    
    if (password !== confirmPassword) {
      setErrors({ confirm: 'Passwords do not match!' });
      return;
    }

    try {
      // Pass password as object parameter
      const resultAction = await dispatch(setPassword({ password }));

      // Check if action was fulfilled
      if (setPassword.fulfilled.match(resultAction)) {
        // Navigate to business profile setup (NOT signin)
        // User needs to complete profile after setting password
        navigate('/business-profile');
      } else {
        // Handle rejection
        const errorMessage = resultAction.payload || 'Failed to create password. Please try again.';
        setErrors({ general: errorMessage });
      }
      
    } catch (error) {
      setErrors({ general: 'Failed to create password. Please try again.' });
    }
  };

  return (
    <div className="create-password-container">
      {/* Left Side - Purple Hero Section */}
      <div className="create-password-left">
        <div className="left-content">
          {/* Logo */}
          <img src={logo} alt="Xtrafyl" className="logo" />

          {/* Hero Text */}
          <div className="hero-section">
            <h1 className="hero-title">
              Fuel Your Business<br />
              With <span className="highlight">Xtrafyl.</span>
            </h1>
            <p className="hero-description text-big-body">
              From fuel delivery to charging, car wash, and more — we help you reach
              more customers, every day.
            </p>
          </div>
        </div>

      </div>

      {/* Right Side - Create Password Form */}
      <div className="create-password-right">
        <div className="create-password-right-content">
          {/* Header */}
          <div className="form-header">
            <h2>Create Password</h2>
            <p className="text-small-body">
              Create a strong and secure password to keep your account safe.
            </p>
          </div>

          <div className="form-container">
            {/* Form */}
            <form onSubmit={handleSubmit} className="create-password-form">
              {/* General Error Message */}
              {(errors.general || error) && (
                <div className="error-banner">
                  {errors.general || error}
                </div>
              )}

              {/* Password Input */}
              <div className="form-group">
                <label htmlFor="password" className="text-label">Password</label>
                <div className={`input-wrapper ${errors.password ? 'input-error' : ''}`}>
                  <img src={passwordIcon} alt="" className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    required
                    className="text-small-body"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7904 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35558 9.06831 4.52744 7.48604 5.95 6.16667M8.25 4.53333C8.82361 4.39907 9.41083 4.33195 10 4.33333C15 4.33333 17.5 10.1667 17.5 10.1667C17.0125 11.0756 16.4208 11.9272 15.7367 12.7033M11.7667 11.7667C11.5379 12.0123 11.2617 12.2093 10.9545 12.3459C10.6474 12.4826 10.316 12.556 9.97979 12.562C9.64359 12.568 9.30979 12.5063 8.99812 12.3806C8.68645 12.2549 8.40348 12.0675 8.16627 11.8303C7.92906 11.5931 7.7417 11.3101 7.61598 10.9985C7.49027 10.6868 7.42859 10.353 7.43458 10.0168C7.44057 9.68062 7.51409 9.34921 7.65074 9.04206C7.78738 8.73491 7.98442 8.45868 8.23 8.23" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="field-error">{errors.password}</span>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="form-group confirm_pass">
                <label htmlFor="confirmPassword" className="text-label">Confirm</label>
                <div className={`input-wrapper ${errors.confirm ? 'input-error' : ''}`}>
                  <img src={passwordIcon} alt="" className="input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="text-small-body"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7904 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35558 9.06831 4.52744 7.48604 5.95 6.16667M8.25 4.53333C8.82361 4.39907 9.41083 4.33195 10 4.33333C15 4.33333 17.5 10.1667 17.5 10.1667C17.0125 11.0756 16.4208 11.9272 15.7367 12.7033M11.7667 11.7667C11.5379 12.0123 11.2617 12.2093 10.9545 12.3459C10.6474 12.4826 10.316 12.556 9.97979 12.562C9.64359 12.568 9.30979 12.5063 8.99812 12.3806C8.68645 12.2549 8.40348 12.0675 8.16627 11.8303C7.92906 11.5931 7.7417 11.3101 7.61598 10.9985C7.49027 10.6868 7.42859 10.353 7.43458 10.0168C7.44057 9.68062 7.51409 9.34921 7.65074 9.04206C7.78738 8.73491 7.98442 8.45868 8.23 8.23" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirm && (
                  <span className="field-error">{errors.confirm}</span>
                )}
              </div>

              {/* Update Password Button */}
              <button
                type="submit"
                className="update-password-button text-button"
                disabled={isLoading}
              >
                {isLoading ? 'CREATING...' : 'UPDATE PASSWORD'}
              </button>

              {/* Sign In Link */}
              <p className="signin-link text-small-body">
                Already have an account? <span onClick={() => navigate('/signin')}>Sign In</span>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;